import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import DataTable from '../../components/ui/DataTable';
import { getStaff, updateStaff, deleteStaff } from '../../services/staff';
import { listLicences } from '../../services/licences';
import { listTranslationRequests } from '../../services/translationRequests';
import { getStatusStyle } from '../../constants/statusConfig';

const PENDING_STATUSES = [
  'Pending',
  'Awaiting Review',
  'Under Review',
  'Re-review',
  'Under Review – Additional Info Required',
  'Under Review - Additional Info Required',
  'Reviewed',
  'Ready for Signature',
  'Terms & Conditions Signed',
];

const formatJoinedDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatTableDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const staffDisplayName = (user) => user?.fullName || user?.name || '—';

const matchesStaffId = (userRef, memberId) =>
  String(userRef?._id || userRef || '') === String(memberId || '');

const getAssignmentRole = (licence, memberId) => {
  const isReviewer = matchesStaffId(licence.reviewerId, memberId);
  const isApprover = matchesStaffId(licence.approverId, memberId);
  if (isReviewer && isApprover) return 'Reviewer & Approver';
  if (isReviewer) return 'Reviewer';
  if (isApprover) return 'Approver';
  return '—';
};

const TeamMemberDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [error, setError] = useState(null);
  const [teamMember, setTeamMember] = useState(null);
  const [licences, setLicences] = useState([]);
  const [translationRequests, setTranslationRequests] = useState([]);
  const [stats, setStats] = useState([
    { label: 'Assigned Licences', value: '0', change: '', isPositive: true },
    { label: 'As Reviewer', value: '0', change: '', isPositive: true },
    { label: 'As Approver', value: '0', change: '', isPositive: true },
    { label: 'Pending Reviews', value: '0', change: '', isPositive: false },
  ]);

  const loadTeamMember = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);

      const [staffRes, licencesRes, translationRes] = await Promise.all([
        getStaff(id),
        listLicences({ assignedUserId: id, page: 1, limit: 500 }),
        listTranslationRequests({ page: 1, limit: 500 }),
      ]);

      const user = staffRes?.data;
      if (!user) {
        setError('Team member not found');
        setTeamMember(null);
        setLicences([]);
        setTranslationRequests([]);
        return;
      }

      const roleNames =
        user.roles?.map((role) => role.name).filter(Boolean).join(', ') ||
        user.roleIds
          ?.map((role) => (typeof role === 'object' ? role.name : role))
          .filter(Boolean)
          .join(', ') ||
        '—';

      const summary = user.assignmentSummary || {};

      setTeamMember({
        id: user._id || user.id,
        name: user.fullName || '—',
        joinedDate: formatJoinedDate(user.createdAt),
        email: user.email || '—',
        phone: user.phone || '—',
        role: roleNames,
        lastSignIn: formatDateTime(user.lastSignInAt),
        emailVerified: user.isEmailVerified ? 'Verified' : 'Not verified',
        accountStatus:
          user.status === 'active'
            ? 'Active'
            : user.status === 'suspended'
              ? 'Suspended'
              : 'Inactive',
        status: user.status || 'active',
        assignedLicences: summary.assignedLicences ?? 0,
        asReviewer: summary.asReviewer ?? 0,
        asApprover: summary.asApprover ?? 0,
        translationRequests: summary.translationRequests ?? 0,
      });

      const licenceList = licencesRes?.data ?? [];
      setLicences(Array.isArray(licenceList) ? licenceList : []);

      const allTranslationRequests = translationRes?.data?.translationRequests || [];
      const assignedTranslations = allTranslationRequests.filter(
        (request) =>
          matchesStaffId(request.reviewerId, id) || matchesStaffId(request.approverId, id),
      );
      setTranslationRequests(assignedTranslations);

      const pendingReviews = (Array.isArray(licenceList) ? licenceList : []).filter((licence) =>
        PENDING_STATUSES.includes(licence.status),
      ).length;
      const apiStats = licencesRes?.stats;

      setStats([
        {
          label: 'Assigned Licences',
          value: String(summary.assignedLicences ?? apiStats?.totalRequests ?? licenceList.length ?? 0),
          change: '',
          isPositive: true,
        },
        {
          label: 'As Reviewer',
          value: String(summary.asReviewer ?? 0),
          change: '',
          isPositive: true,
        },
        {
          label: 'As Approver',
          value: String(summary.asApprover ?? 0),
          change: '',
          isPositive: true,
        },
        {
          label: 'Pending Reviews',
          value: String(apiStats?.pending ?? pendingReviews),
          change: '',
          isPositive: false,
        },
      ]);
    } catch (err) {
      console.error('Failed to load team member details:', err);
      setError(err?.data?.error || err?.message || 'Failed to load team member details');
      setTeamMember(null);
      setLicences([]);
      setTranslationRequests([]);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadTeamMember();
  }, [loadTeamMember]);

  const handleDeleteAccount = async () => {
    try {
      await deleteStaff(id);
      setIsDeleteModalOpen(false);
      navigate('/settings/admin-team');
    } catch (err) {
      console.error('Failed to delete team member:', err);
      alert(err?.data?.error || err?.message || 'Failed to delete team member');
    }
  };

  const handleStatusChange = async (event) => {
    const nextStatus = event.target.value;
    if (!teamMember || nextStatus === teamMember.status) return;

    try {
      setSavingStatus(true);
      await updateStaff(id, { status: nextStatus });
      setTeamMember((prev) => ({
        ...prev,
        status: nextStatus,
        accountStatus:
          nextStatus === 'active' ? 'Active' : nextStatus === 'suspended' ? 'Suspended' : 'Inactive',
      }));
    } catch (err) {
      console.error('Failed to update member status:', err);
      alert(err?.data?.error || err?.message || 'Failed to update member status');
    } finally {
      setSavingStatus(false);
    }
  };

  const licenceRows = licences.map((licence) => {
    const customerName = licence.customerId
      ? [licence.customerId.firstName, licence.customerId.lastName].filter(Boolean).join(' ').trim() ||
        licence.contact?.fullName ||
        '—'
      : licence.contact?.fullName || '—';

    return {
      id: licence._id,
      assignment: getAssignmentRole(licence, id),
      reviewer: staffDisplayName(licence.reviewerId),
      approver: staffDisplayName(licence.approverId),
      ref: licence.refNumber || '—',
      name: customerName,
      licenceName: licence.coaId?.name || '—',
      status: licence.status || 'Pending',
      date: formatTableDate(licence.submittedAt || licence.createdAt),
    };
  });

  const translationRows = translationRequests.map((request) => ({
    id: request._id,
    ref: request.refNumber || '—',
    licenceRef: request.licenceId?.refNumber || '—',
    coaName: request.coaId?.name || '—',
    assignment: getAssignmentRole(request, id),
    status: request.translationStatus || request.status || 'Pending',
    date: formatTableDate(request.submittedAt || request.createdAt),
  }));

  const renderStatusBadge = (status) => {
    const { bg, text, label } = getStatusStyle(status);
    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border ${bg} ${text} border-gray-200`}>
        {label || status || '—'}
      </span>
    );
  };

  const licenceColumns = [
    {
      header: 'Assignment',
      accessor: 'assignment',
      className: 'text-gray-900 font-medium',
    },
    { header: 'Reviewer', accessor: 'reviewer', className: 'text-gray-900' },
    { header: 'Approver', accessor: 'approver', className: 'text-gray-900' },
    { header: 'Ref #', accessor: 'ref', className: 'text-gray-900' },
    { header: 'Customer', accessor: 'name', className: 'text-gray-900' },
    { header: 'Licence Name', accessor: 'licenceName', className: 'text-gray-600' },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => renderStatusBadge(value),
    },
    { header: 'Date', accessor: 'date', className: 'text-gray-600' },
    {
      header: 'Action',
      accessor: 'action',
      render: (_, row) => (
        <button
          type="button"
          onClick={() => navigate(`/licences/${row.id}`)}
          className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
        >
          View
        </button>
      ),
    },
  ];

  const translationColumns = [
    { header: 'Ref #', accessor: 'ref', className: 'text-gray-900' },
    { header: 'Licence Ref', accessor: 'licenceRef', className: 'text-gray-900' },
    { header: 'COA', accessor: 'coaName', className: 'text-gray-600' },
    { header: 'Assignment', accessor: 'assignment', className: 'text-gray-900 font-medium' },
    {
      header: 'Status',
      accessor: 'status',
      render: (value) => renderStatusBadge(value),
    },
    { header: 'Submitted', accessor: 'date', className: 'text-gray-600' },
    {
      header: 'Action',
      accessor: 'action',
      render: (_, row) => (
        <button
          type="button"
          onClick={() => navigate(`/translation-request/${row.id}`)}
          className="text-sm font-medium text-emerald-600 hover:text-emerald-700"
        >
          View
        </button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto py-16 text-center text-gray-500">
        Loading team member details...
      </div>
    );
  }

  if (error || !teamMember) {
    return (
      <div className="max-w-[1400px] mx-auto space-y-4 py-8">
        <button type="button" onClick={() => navigate('/settings/admin-team')} className="text-sm text-gray-600 hover:text-gray-900">
          ← Back to Admin Team
        </button>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-red-700">
          {error || 'Team member not found'}
        </div>
      </div>
    );
  }

  const initials =
    teamMember.name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || '?';

  return (
    <div className="max-w-[1400px] mx-auto space-y-6 pb-8">
      <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
        <button type="button" onClick={() => navigate('/settings/admin-team')} className="hover:text-gray-900">
          ← Admin Team
        </button>
        <span>/</span>
        <span className="text-gray-900">Team Member View Details</span>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {stats.map((stat) => (
          <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M11.6667 1.66666H5.00001C4.55798 1.66666 4.13406 1.84225 3.82149 2.15481C3.50893 2.46737 3.33334 2.89129 3.33334 3.33332V16.6667C3.33334 17.1087 3.50893 17.5326 3.82149 17.8452C4.13406 18.1577 4.55798 18.3333 5.00001 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66666L11.6667 1.66666Z" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M11.6667 1.66666V6.66666H16.6667" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13.3333 10.8333H6.66666" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13.3333 14.1667H6.66666" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8.33334 7.5H7.50001H6.66668" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
            <p className="text-3xl font-semibold text-gray-900 mb-1">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-[1fr,320px] gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center">
                <span className="text-3xl font-semibold text-gray-600">{initials}</span>
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-900 mb-1">{teamMember.name}</h2>
                <p className="text-sm text-gray-500">Joined: {teamMember.joinedDate}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                disabled
                title="Password reset is not available yet"
                className="px-5 py-2.5 text-sm font-medium text-gray-400 border border-gray-200 rounded-lg cursor-not-allowed"
              >
                Reset Password
              </button>
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                className="px-5 py-2.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600"
              >
                Delete Account
              </button>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-x-8 gap-y-5">
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Email Address</p>
              <p className="text-sm text-gray-900">{teamMember.email}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Phone Number</p>
              <p className="text-sm text-gray-900">{teamMember.phone}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Role</p>
              <p className="text-sm text-gray-900">{teamMember.role}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Email Verification</p>
              <p className="text-sm text-gray-900">{teamMember.emailVerified}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Account Status</p>
              <p className="text-sm text-gray-900">{teamMember.accountStatus}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Last Sign In</p>
              <p className="text-sm text-gray-900">{teamMember.lastSignIn}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Translation Requests</p>
              <p className="text-sm text-gray-900">{teamMember.translationRequests}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Assigned Licences</p>
              <p className="text-sm text-gray-900">{teamMember.assignedLicences}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1.5">Reviewer / Approver</p>
              <p className="text-sm text-gray-900">
                {teamMember.asReviewer} / {teamMember.asApprover}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2.5">Member Status</label>
            <select
              value={teamMember.status}
              onChange={handleStatusChange}
              disabled={savingStatus}
              className="w-full px-3.5 py-2 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white disabled:opacity-60"
            >
              <option value="active">Available</option>
              <option value="inactive">Unavailable</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Assigned Licences</h3>
        <p className="text-sm text-gray-500 mb-4">
          Licence requests where this team member is assigned as reviewer and/or approver.
        </p>
        {licenceRows.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
            No licences assigned to this team member yet.
          </div>
        ) : (
          <DataTable
            data={licenceRows}
            columns={licenceColumns}
            showPagination={licenceRows.length > 10}
            rowsPerPage={10}
            containerClassName="bg-white rounded-xl shadow-sm border border-gray-200 relative z-0"
            tableWrapperClassName="overflow-x-auto overflow-visible"
          />
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Assigned Translation Requests</h3>
        <p className="text-sm text-gray-500 mb-4">
          Translation requests linked to this team member as reviewer or approver.
        </p>
        {translationRows.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
            No translation requests assigned to this team member yet.
          </div>
        ) : (
          <DataTable
            data={translationRows}
            columns={translationColumns}
            showPagination={translationRows.length > 10}
            rowsPerPage={10}
            containerClassName="bg-white rounded-xl shadow-sm border border-gray-200 relative z-0"
            tableWrapperClassName="overflow-x-auto overflow-visible"
          />
        )}
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteAccount}
        title="Delete Team Member Account"
        message="Are you sure you want to delete this team member account? This action cannot be undone."
        itemName={teamMember.name}
      />
    </div>
  );
};

export default TeamMemberDetails;
